import { SimpleCard } from "./DataCard";

const practicalCards = [
  {
    title: "1. A December dip is normal, not a red flag",
    content: "When responses in December are lower than in previous months, this aligns with industry wide data. Deliverability can be healthy, but engagement will still drop if your messages are competing inside a crowded Promotions tab while customers are busy with year end and holidays."
  },
  {
    title: "2. Deliverability checks still matter",
    content: "It is still important to monitor technical deliverability, DNS configuration, spam complaints, bounce rates, and blocklists. If these indicators are within normal ranges and your messages appear in Promotions instead of Spam, then the main limitation in December is user attention and timing, not technical failure."
  }
];

const bestPractices = [
  "Send key campaigns a little earlier in the season where possible.",
  "Test off peak send times such as evening or early morning.",
  "Use very focused subject lines and clear calls to action.",
  "Avoid unnecessary frequency that increases fatigue and unsubscribes."
];

const sources = [
  {
    text: "Validity – 2024/2025 Email Deliverability Benchmark with ISP-specific inbox placement rates:",
    links: [
      { label: "2025 Deliverability Benchmark", url: "https://www.validity.com/resource-center/2025-email-deliverability-benchmark-report/" },
      { label: "Sender Reputation Guide", url: "https://www.validity.com/e-mail-marketing/sender-reputation/" }
    ]
  },
  {
    text: "GetResponse – Email marketing benchmarks based on billions of emails:",
    links: [
      { label: "Email Marketing Benchmarks", url: "https://www.getresponse.com/resources/reports/email-marketing-benchmarks" },
      { label: "Key Statistics Report", url: "https://www.getresponse.com/blog/email-marketing-statistics" }
    ]
  },
  {
    text: "Smart Insights – E-commerce conversion rate benchmarks by month and season:",
    links: [
      { label: "E-commerce Conversion Rates", url: "https://www.smartinsights.com/ecommerce/ecommerce-analytics/ecommerce-conversion-rates/" }
    ]
  },
  {
    text: "Sinch Mailjet – Black Friday/Cyber Monday email performance statistics:",
    links: [
      { label: "BFCM Stats 2025", url: "https://www.mailjet.com/blog/email-best-practices/bfcm-stats-2025/" }
    ]
  },
  {
    text: "MailerLite – Email marketing benchmarks by industry including open/click rates:",
    links: [
      { label: "Email Benchmarks by Industry", url: "https://www.mailerlite.com/blog/email-marketing-statistics" }
    ]
  },
  {
    text: "Litmus – State of Email 2025 report on AI filtering and privacy changes:",
    links: [
      { label: "State of Email Report", url: "https://www.litmus.com/resources/state-of-email" }
    ]
  }
];

export const PracticalSection = () => {
  return (
    <section className="section-card mt-5 sm:mt-7">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1.5 sm:gap-2 mb-5 sm:mb-6">
        <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight text-foreground">
          What this means for your campaigns
        </h2>
        <p className="text-[11px] sm:text-xs text-muted-foreground">
          How to interpret December performance fairly
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
        {practicalCards.map((card, i) => (
          <SimpleCard key={i} title={card.title}>
            <p className="text-xs text-muted-foreground leading-relaxed">{card.content}</p>
          </SimpleCard>
        ))}

        <SimpleCard title="3. Best practice for the holiday period">
          <ul className="list-none space-y-1.5">
            {bestPractices.map((practice, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                <span className="w-1 h-1 rounded-full bg-success mt-1.5 shrink-0" />
                {practice}
              </li>
            ))}
          </ul>
        </SimpleCard>

        <SimpleCard title="4. How we evaluate success">
          <p className="text-xs text-muted-foreground leading-relaxed">
            We compare your campaigns against realistic benchmarks by industry and 
            season, rather than treating December in isolation. This helps separate
            structural seasonal effects from genuine issues like list quality or 
            infrastructure problems.
          </p>
        </SimpleCard>
      </div>

      {/* Footnotes */}
      <div className="mt-6 sm:mt-8 pt-5 border-t border-border">
        <h3 className="font-display text-sm font-semibold text-foreground mb-3">
          Sources and further reading
        </h3>
        <div className="space-y-1.5 text-[10px] sm:text-[11px] text-muted-foreground">
          {sources.map((source, i) => (
            <p key={i} className="leading-relaxed">
              • {source.text}{" "}
              {source.links.map((link, j) => (
                <span key={j}>
                  <a 
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline transition-colors"
                  >
                    {link.label}
                  </a>
                  {j < source.links.length - 1 && " and "}
                </span>
              ))}
              .
            </p>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
          These sources show that December performance is shaped strongly by seasonality, higher
          sending volumes, and stricter filters, even when technical setup is correct.
        </p>
      </div>
    </section>
  );
};